import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { FloatingList } from '@ipfabric/ui-components/List/FloatingList/FloatingList';
import type { FloatingListItem } from '@ipfabric/ui-components/List/FloatingList/FloatingList.types';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import { SegmentedButton } from '@ipfabric/ui-components/SegmentedButton/SegmentedButton';
import {
  DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  useDebounce,
} from '@ipfabric/ui-components/hooks/useDebounce';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import classNames from 'classnames';
import { useLocale } from 'containers/App/LocaleContext';
import elementResizeDetectorMaker from 'element-resize-detector';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { JSX } from 'react';
import type { AdvancedFilterColumnRecord } from './AdvancedFilter';
import { FilterRule } from './FilterRule';
import {
  type GroupFilter,
  type RuleFilter,
  emptyRuleFilter,
  isGroupFilterOperator,
} from './useAdvancedFilter';

interface Props<TRow> {
  filter: GroupFilter;
  path: number[];
  onChange: (path: number[], newFilter: GroupFilter | RuleFilter) => void;
  onDelete: (path: number[]) => void;
  columns: AdvancedFilterColumnRecord<TRow>;
  clipboard: GroupFilter | null;
  copy: (group: GroupFilter) => void;
  draggable?: boolean;
  disableColorOperators?: boolean;
  previewBtn?: ReactNode;
}

const sortFilters = (
  a: GroupFilter | RuleFilter,
  b: GroupFilter | RuleFilter,
): number => (a.type === 'ruleFilter' && b.type === 'groupFilter' ? -1 : 0);

export const FilterGroup = <TRow,>(props: Props<TRow>): JSX.Element => {
  const { advancedFilter: advancedFilterMsg } = useLocale();
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null);
  const [wrapped, setWrapped] = useState(false);
  const debounce = useDebounce(DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT);
  const handleOperatorChange = (value: string) => {
    if (!isGroupFilterOperator(value)) {
      stumble('unknown operator value');
      return;
    }
    props.onChange(props.path, {
      ...props.filter,
      operator: value,
    });
  };

  const addGroup = () => {
    const { newFilter } = produce({ newFilter: props.filter }, (draft) => {
      draft.newFilter.filters.push({
        type: 'groupFilter',
        operator: 'and',
        filters: [emptyRuleFilter],
      });
      draft.newFilter.filters = draft.newFilter.filters.sort(sortFilters);
    });

    props.onChange(props.path, newFilter);
  };

  const addRule = () => {
    const { newFilter } = produce({ newFilter: props.filter }, (draft) => {
      draft.newFilter.filters.push(emptyRuleFilter);
      draft.newFilter.filters = draft.newFilter.filters.sort(sortFilters);
    });

    props.onChange(props.path, newFilter);
  };

  const overflowMenuItems: FloatingListItem[] = [
    {
      label: advancedFilterMsg.contextMenu.copy,
      onClick: () => {
        props.copy(props.filter);
      },
    },
    {
      label: advancedFilterMsg.contextMenu.pasteRules,
      disabled: props.clipboard === null,
      onClick: () => {
        if (props.clipboard) {
          props.onChange(props.path, {
            ...props.filter,
            filters: [...props.filter.filters, ...props.clipboard.filters],
          });
        }
      },
    },
    {
      label: advancedFilterMsg.contextMenu.pasteGroup,
      disabled: props.clipboard === null,
      onClick: () => {
        if (props.clipboard) {
          props.onChange(props.path, {
            ...props.filter,
            filters: [...props.filter.filters, props.clipboard],
          });
        }
      },
    },
    {
      label: advancedFilterMsg.contextMenu.pasteAndReplace,
      disabled: props.clipboard === null,
      onClick: () => {
        if (props.clipboard) {
          props.onChange(props.path, props.clipboard);
        }
      },
    },
    { divider: true },
    {
      label: advancedFilterMsg.contextMenu.deleteGroup,
      className: props.path.length === 0 ? undefined : 'text-danger',
      disabled: props.path.length === 0,
      onClick: () => {
        props.onDelete(props.path);
      },
    },
  ];

  const handleRef = useStableCallback((node: HTMLElement | null) => {
    setWrapper(node);
  });

  useEffect(() => {
    if (!wrapper) {
      return () => {};
    }

    const erd = elementResizeDetectorMaker({
      // recommended default for best performance according to docs
      strategy: 'scroll',
    });
    erd.listenTo(wrapper, (el) => {
      if (el.clientWidth < 635) {
        debounce(() => {
          setWrapped(true);
        });
      } else {
        debounce(() => {
          setWrapped(false);
        });
      }
    });

    return () => {
      erd.uninstall(wrapper);
    };
  }, [wrapper]);

  return (
    <div
      className={classNames(
        'filter-group',
        props.draggable && 'filter-group--nested',
      )}
      ref={handleRef}
    >
      <div className='filter-group__header'>
        <SegmentedButton.Single
          value={props.filter.operator}
          size='small'
          onChange={handleOperatorChange}
        >
          <SegmentButton value='and'>And</SegmentButton>
          <SegmentButton value='or'>Or</SegmentButton>
        </SegmentedButton.Single>
        <div className='flex flex-wrap'>
          <Button
            type='button'
            variant='plain'
            onClick={() => {
              addGroup();
            }}
          >
            {advancedFilterMsg.actions.addGroup}
          </Button>
          <Button
            type='button'
            variant='plain'
            onClick={() => {
              addRule();
            }}
          >
            {advancedFilterMsg.actions.addRule}
          </Button>
          <FloatingList items={overflowMenuItems}>
            {(setRef, setIsOpen, open) => (
              <Button
                variant='plain'
                forwardRef={setRef}
                onClick={() => {
                  setIsOpen(!open);
                }}
                icon='fa-ellipsis'
              />
            )}
          </FloatingList>
        </div>
      </div>
      <div
        className={classNames(
          'filter-group__content',
          wrapped && 'filter-group__content--wrapped',
        )}
      >
        {props.filter.filters.map((filter, index) => {
          const path = [...props.path, index];

          return filter.type === 'groupFilter' ? (
            <FilterGroup
              {...props}
              filter={filter}
              path={[...props.path, index]}
              key={path.join('.')}
              draggable
              previewBtn={undefined}
            />
          ) : (
            <FilterRule
              {...props}
              filter={filter}
              path={[...props.path, index]}
              key={path.join('.')}
              draggable
            />
          );
        })}
      </div>
      {props.previewBtn && <div className='mx-4 mb-4'>{props.previewBtn}</div>}
    </div>
  );
};
